import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation, ACTIVE_LANGUAGES } from '../lib/i18n';
import api from '../lib/api';
import AppSidebar from './AppSidebar';
import './AppSidebar.css';

const SIDEBAR_COLLAPSED_KEY = 'easeverse_reader_sidebar_collapsed';

function usePersistedSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  }, []);

  return { collapsed, toggle };
}

function AppLayout() {
  const { collapsed, toggle } = usePersistedSidebarCollapsed();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { getToken } = useAuth();
  const { t, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const localeSyncedRef = useRef(false);

  // Sync locale from backend on first authenticated load
  useEffect(() => {
    const token = getToken();
    if (!token || localeSyncedRef.current) return;
    localeSyncedRef.current = true;
    api.getSettings().then((data) => {
      if (data?.settings?.locale && ACTIVE_LANGUAGES.includes(data.settings.locale)) {
        setLanguage(data.settings.locale);
      }
    }).catch(() => {});
  }, [getToken, setLanguage]);

  return (
    <div className="app-layout-root">
      {/* Mobile-only header with hamburger */}
      <header className="app-layout-header app-layout-header-mobile">
        <button
          className="app-header-hamburger"
          onClick={() => setSidebarOpen(true)}
          aria-label="Menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <button
          type="button"
          className="app-header-brand"
          onClick={() => navigate('/')}
          aria-label={t('library.backToHome')}
        >
          <img src="/EaseVerse_Reader_ClassicBlue_Depth_v3.jpg" alt="" className="app-header-logo" />
          <h1>EaseVerse</h1>
        </button>
        <div className="app-header-spacer" />
      </header>

      <div className="app-layout-body">
        <AppSidebar
          collapsed={collapsed}
          onToggleCollapse={toggle}
          sidebarOpen={sidebarOpen}
          onCloseSidebar={() => setSidebarOpen(false)}
        />
        {/* Collapse toggle — lives outside sidebar to avoid overflow clipping */}
        <button
          className="app-sidebar-toggle"
          onClick={toggle}
          title={t(collapsed ? 'sidebar.expand' : 'sidebar.collapse')}
          style={{ left: collapsed ? 52 : 180 }}
        >
          {collapsed ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          )}
        </button>
        <main className="app-layout-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
